import { UISubscriptionStatus } from "common/types";
import { useNavigate } from "common/router/hooks";
import Navigate from "common/router/Navigate";
import { Destination } from "common/router/types";

import Button from "client/common/components/buttons/Button";
import { urls } from "client/routes";

import {
  OnboardingSubscriptionsStepParams,
  OnboardingDetailSubscriptionStep,
  OnboardingDetailSubscriptionStepParams,
  IOnboardingSubscription,
} from "../../types";
import OnboardingSubscriptionQuantityFrequencyStep from "./OnboardingSubscriptionQuantityFrequencyStep";
import OnboardingSubscriptionNextOrderDateStep from "./OnboardingSubscriptionNextOrderDateStep";
import OnboardingSubscriptionCancelStep from "./OnboardingSubscriptionCancelStep";
import StepLayout from "../OnboardingSubscriptionsStepLayout";
import OnboardingSubscriptionItem from "../OnboardingSubscriptionItem";
import OnboardingSubscriptionsPatientMissingFieldsStep from "./OnboardingSubscriptionsPatientMissingFieldsStep";

type DetailStepDefinition = {
  body: (props: OnboardingDetailSubscriptionStepParams) => React.ReactElement;
};

const detailStepToDefinition: {
  [K in OnboardingDetailSubscriptionStep]: DetailStepDefinition;
} = {
  [OnboardingDetailSubscriptionStep.QuantityFrequency]: {
    body: (props) => <OnboardingSubscriptionQuantityFrequencyStep {...props} />,
  },
  [OnboardingDetailSubscriptionStep.NextOrderDate]: {
    body: (props) => <OnboardingSubscriptionNextOrderDateStep {...props} />,
  },
  [OnboardingDetailSubscriptionStep.Cancel]: {
    body: (props) => <OnboardingSubscriptionCancelStep {...props} />,
  },
  [OnboardingDetailSubscriptionStep.PatientMissingFields]: {
    body: (props) => <OnboardingSubscriptionsPatientMissingFieldsStep {...props} />,
  },
};

type OnboardingSubscriptionCanceledStepProps = {
  subscription: IOnboardingSubscription;
  stepper: React.ReactNode;
  rootDest: Destination;
};

const OnboardingSubscriptionCanceledStep = ({
  subscription,
  stepper,
  rootDest,
}: OnboardingSubscriptionCanceledStepProps) => {
  const navigate = useNavigate();
  return (
    <StepLayout stepper={stepper}>
      <StepLayout.Title>
        Your Autoship has been canceled and cannot be further modified. You may set a new Autoship
        order at any time by visiting our online store.
      </StepLayout.Title>
      <OnboardingSubscriptionItem subscription={subscription}>
        <OnboardingSubscriptionItem.Pricing subscription={subscription} />
      </OnboardingSubscriptionItem>
      <StepLayout.Footer>
        <Button onClick={() => navigate(rootDest)}>Continue</Button>
      </StepLayout.Footer>
    </StepLayout>
  );
};

type OnboardingSubscriptionStepProps = OnboardingSubscriptionsStepParams & {
  step: OnboardingDetailSubscriptionStep;
};

const OnboardingSubscriptionStep = (props: OnboardingSubscriptionStepProps) => {
  const { step, id, onboarding, stepper, rootDest } = props;
  if (!id) {
    console.warn(`No subscription id provided for ${step} step`);
    return <Navigate dest={urls.onboarding.subscriptions.error()} />;
  }
  const subscription = onboarding.subscriptions.find((sub) => sub.id === id);
  if (!subscription) {
    console.warn(`Subscription id not found for ${step} step`);
    return <Navigate dest={urls.onboarding.subscriptions.error()} />;
  }

  const { body } = detailStepToDefinition[step];
  if (subscription.visible_status === UISubscriptionStatus.Canceled) {
    return (
      <OnboardingSubscriptionCanceledStep
        subscription={subscription}
        stepper={stepper}
        rootDest={rootDest}
      />
    );
  }

  return body({ ...props, id, subscription });
};

export default OnboardingSubscriptionStep;
